import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import API from '../utils/api';

async function registerFcmToken(uniqueId) {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform()) return;

    await PushNotifications.requestPermissions();
    await PushNotifications.register();
    PushNotifications.addListener('registration', async ({ value: token }) => {
      await API.post(`/users/${uniqueId}/fcm-token`, { fcmToken: token });
    });
  } catch {}
}

const SocketCtx = createContext(null);

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const socketRef          = useRef(null);
  const currentRoomRef     = useRef(null);
  const activeChatRoomRef  = useRef(null);
  const notifTimer         = useRef(null);
  const peekTimer          = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [unreadCounts,  setUnreadCounts]  = useState({});
  const [notification,  setNotification]  = useState(null);
  const [presence,      setPresence]      = useState(new Map()); // uniqueId → 'online' | 'away'
  const [notifs,        setNotifs]        = useState([]);
  const [peekNotif,     setPeekNotif]     = useState(null);

  // ── Global call state ─────────────────────────────────────────────────────
  const [incomingCall, setIncomingCall] = useState(null);
  const [pendingCall,  setPendingCall]  = useState(null);

  const totalUnread = Object.values(unreadCounts).reduce((s, n) => s + n, 0);
  const notifUnread = notifs.filter(n => !n.read).length;

  // ── Fetch conversations and join their rooms ──────────────────────────────
  const fetchAndJoin = useCallback(async () => {
    if (!user || !socketRef.current) return;
    try {
      const res = await API.get(`/chat/conversations/${user.uniqueId}`);
      setConversations(res.data);
      res.data.forEach(c => socketRef.current?.emit('join_room', c.chatRoomId));
    } catch {}
  }, [user?.uniqueId]); // eslint-disable-line

  const fetchNotifs = useCallback(async () => {
    if (!user) return;
    try {
      const res = await API.get('/notifications?limit=30');
      setNotifs(res.data);
    } catch {}
  }, [user?.uniqueId]); // eslint-disable-line

  // ── Connect / disconnect when auth changes ────────────────────────────────
  useEffect(() => {
    if (!user) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setConversations([]);
      setUnreadCounts({});
      setNotification(null);
      setPresence(new Map());
      setIncomingCall(null);
      setPendingCall(null);
      setNotifs([]);
      setPeekNotif(null);
      return;
    }

    const proto  = window.location.protocol === 'https:' ? 'https' : 'http';
    const isDev  = window.location.port === '3000';
    const origin = isDev ? `${proto}://${window.location.hostname}:5000` : window.location.origin;
    // The server refuses unauthenticated sockets. auth is a callback so every
    // (re)connect sends the CURRENT token, not the one present at creation.
    const socket = io(origin, { auth: (cb) => cb({ token: localStorage.getItem('wt_token') || '' }) });
    socketRef.current = socket;

    // The server force-closes a socket whose session was revoked (PIN reset,
    // mobile change) or whose token expired, and socket.io never reconnects
    // that on its own. Retry once with whatever token is stored now; a revoked
    // one is refused and the next REST 401 signs the user out.
    socket.on('disconnect', (reason) => {
      if (reason !== 'io server disconnect') return;
      setTimeout(() => {
        if (socketRef.current === socket && localStorage.getItem('wt_token')) socket.connect();
      }, 2000);
    });

    socket.on('connect', () => {
      fetchAndJoin();
      fetchNotifs();
      socket.emit('user_online', user.uniqueId);
      registerFcmToken(user.uniqueId);
    });

    // Full snapshot — only sent to this socket on initial connect
    socket.on('presence_update', ({ online = [], away = [] }) => {
      setPresence(() => {
        const m = new Map();
        online.forEach(id => m.set(id, 'online'));
        away.forEach(id   => m.set(id, 'away'));
        return m;
      });
    });

    // Incremental delta — one user changed state; update map in-place
    socket.on('presence_delta', ({ online = [], away = [], offline = [] }) => {
      setPresence(prev => {
        const m = new Map(prev);
        online.forEach(id  => m.set(id, 'online'));
        away.forEach(id    => m.set(id, 'away'));
        offline.forEach(id => m.delete(id));
        return m;
      });
    });

    socket.on('new_message', (data) => {
      if (data.senderId === user.uniqueId) return;
      setConversations(prev =>
        prev.map(c =>
          c.chatRoomId === data.roomId
            ? { ...c, lastMessage: data, lastActivity: new Date().toISOString() }
            : c
        )
      );
      if (currentRoomRef.current !== data.roomId) {
        setUnreadCounts(prev => ({ ...prev, [data.roomId]: (prev[data.roomId] || 0) + 1 }));
      }
    });

    socket.on('new_notification', (notif) => {
      setNotifs(prev => [notif, ...prev]);
      clearTimeout(peekTimer.current);
      setPeekNotif(notif);
      peekTimer.current = setTimeout(() => setPeekNotif(null), 3500);
    });

    // ── Global call routing ───────────────────────────────────────────────
    socket.on('call_offer', (data) => {
      if (activeChatRoomRef.current === data.roomId) {
        window.dispatchEvent(new CustomEvent('wishtree:call_offer', { detail: data }));
      } else {
        setIncomingCall(data);
      }
    });

    socket.on('call_unavailable', (data) => {
      window.dispatchEvent(new CustomEvent('wishtree:call_unavailable', { detail: data }));
    });

    socket.on('chat_deleted', ({ roomId }) => {
      setConversations(prev => prev.filter(c => c.chatRoomId !== roomId));
      setUnreadCounts(prev => { const next = { ...prev }; delete next[roomId]; return next; });
    });

    const handleNewChat = () => fetchAndJoin();
    window.addEventListener('wishtree:new_chat', handleNewChat);

    // ── Page Visibility — tab hidden → away, tab visible → online or logout ──
    const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        socket.emit('user_away', user.uniqueId);
      } else {
        const last = parseInt(localStorage.getItem('wishtree_lastActiveAt') || '0', 10);
        if (last > 0 && Date.now() - last > TWO_HOURS_MS) {
          window.location.href = '/join';
          return;
        }
        socket.emit('user_online', user.uniqueId);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      socket.disconnect();
      socketRef.current = null;
      window.removeEventListener('wishtree:new_chat', handleNewChat);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearTimeout(notifTimer.current);
      clearTimeout(peekTimer.current);
    };
  }, [user?.uniqueId]); // eslint-disable-line

  // ── Called by PrivateChat on mount/unmount to register active room ────────
  const setActiveChatRoom  = useCallback((roomId) => { activeChatRoomRef.current = roomId; }, []);
  const clearActiveChatRoom = useCallback(() => { activeChatRoomRef.current = null; }, []);

  // ── Global overlay actions ────────────────────────────────────────────────
  const acceptGlobalCall = useCallback(() => {
    if (!incomingCall) return;
    setPendingCall(incomingCall);
    setIncomingCall(null);
  }, [incomingCall]);

  const rejectGlobalCall = useCallback(() => {
    if (!incomingCall) return;
    socketRef.current?.emit('call_reject', { roomId: incomingCall.roomId });
    setIncomingCall(null);
  }, [incomingCall]);

  const clearPendingCall = useCallback(() => setPendingCall(null), []);

  // ── Chat room read tracking ───────────────────────────────────────────────
  const markRoomRead = useCallback((roomId) => {
    currentRoomRef.current = roomId;
    setUnreadCounts(prev => { const next = { ...prev }; delete next[roomId]; return next; });
  }, []);

  const clearCurrentRoom = useCallback(() => { currentRoomRef.current = null; }, []);

  const dismissNotification = useCallback(() => {
    clearTimeout(notifTimer.current);
    setNotification(null);
  }, []);

  const dismissPeek = useCallback(() => {
    clearTimeout(peekTimer.current);
    setPeekNotif(null);
  }, []);

  const markAllNotifsRead = useCallback(async () => {
    if (!user) return;
    try {
      await API.post('/notifications/read-all', {});
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  }, [user?.uniqueId]); // eslint-disable-line

  return (
    <SocketCtx.Provider value={{
      socket:             socketRef.current,
      conversations,
      setConversations,
      fetchConversations: fetchAndJoin,
      unreadCounts,
      totalUnread,
      markRoomRead,
      clearCurrentRoom,
      notification,
      dismissNotification,
      notifs,
      notifUnread,
      peekNotif,
      dismissPeek,
      markAllNotifsRead,
      presence,
      // call globals
      incomingCall,
      pendingCall,
      acceptGlobalCall,
      rejectGlobalCall,
      clearPendingCall,
      setActiveChatRoom,
      clearActiveChatRoom,
    }}>
      {children}
    </SocketCtx.Provider>
  );
}

export const useSocket = () => useContext(SocketCtx);
